import { useState, useEffect } from 'react';

/**
 * useInputMode — Context-Aware Input Detection
 *
 * Listens for the user's actual first interaction to determine their preferred
 * input mode, rather than guessing from device capabilities at mount time.
 *
 * Behavior:
 * - Default: 'keyboard' (majority of users are on desktop)
 * - First keydown → sets 'keyboard' and persists to sessionStorage
 * - First touchstart → sets 'touch' and persists to sessionStorage
 * - sessionStorage key 'inputMode' restores preference on page reload
 *   (within the same browser session — not persisted across sessions)
 *
 * The state updates trigger a re-render in any component that calls this hook,
 * so the HUD instruction text swaps in real-time on first interaction.
 */

export type InputMode = 'keyboard' | 'touch' | 'gamepad';

const SESSION_KEY = 'inputMode';

function getInitialMode(): InputMode {
  // Restore from sessionStorage if the user has already interacted this session
  try {
    const stored = sessionStorage.getItem(SESSION_KEY);
    if (stored === 'keyboard' || stored === 'touch' || stored === 'gamepad') return stored;
  } catch {
    // sessionStorage unavailable (private browsing, some embedded contexts)
  }
  // Default to keyboard — most users are on desktop
  return 'keyboard';
}

export function useInputMode(): InputMode {
  const [mode, setMode] = useState<InputMode>(getInitialMode);

  useEffect(() => {
    let settled = false;

    const handleKeyDown = () => {
      if (settled) return;
      settled = true;
      setMode('keyboard');
      try { sessionStorage.setItem(SESSION_KEY, 'keyboard'); } catch { /* ok */ }
      // Once settled, remove both listeners — no need to keep polling
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('touchstart', handleTouchStart);
    };

    const handleTouchStart = () => {
      if (settled) return;
      settled = true;
      setMode('touch');
      try { sessionStorage.setItem(SESSION_KEY, 'touch'); } catch { /* ok */ }
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('touchstart', handleTouchStart);
    };

    // Gamepad: fires when a controller is connected (any time, not just first session)
    const handleGamepadConnected = () => {
      setMode('gamepad');
      try { sessionStorage.setItem(SESSION_KEY, 'gamepad'); } catch { /* ok */ }
    };
    window.addEventListener('gamepadconnected', handleGamepadConnected);

    // Only attach keyboard/touch listeners if the mode hasn't been determined yet this session
    const stored = (() => {
      try { return sessionStorage.getItem(SESSION_KEY); } catch { return null; }
    })();

    if (!stored) {
      // First visit this session — listen for the first real interaction
      window.addEventListener('keydown', handleKeyDown, { passive: true });
      window.addEventListener('touchstart', handleTouchStart, { passive: true });
    }

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('touchstart', handleTouchStart);
      window.removeEventListener('gamepadconnected', handleGamepadConnected);
    };
  }, []);

  return mode;
}
